import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { loginRequest } from "../auth/auth-config";
import { msalInstance, msalInitPromise } from "../auth/msal-instance";
import axios from "axios";
import i18n from "../i18n/i18n";
import { LanguageCode } from "../models/enums/LanguageCode";

const API_URL = process.env.REACT_APP_API_URL;

const apiClient = axios.create({
	baseURL: API_URL
});

// Register interceptor immediately, but wait for MSAL init inside
apiClient.interceptors.request.use(async config => {
	// Wait for MSAL to be initialized before trying to get token
	await msalInitPromise;

	// Add authorization token
	let account = msalInstance.getActiveAccount();

	// If no active account, try to get one from all accounts
	if (!account) {
		const accounts = msalInstance.getAllAccounts();
		if (accounts.length > 0) {
			account = accounts[0];
			msalInstance.setActiveAccount(account);
		}
	}

	if (account) {
		try {
			const tokenResponse = await msalInstance.acquireTokenSilent({
				account: account,
				scopes: loginRequest.scopes
			});
			config.headers.Authorization = `Bearer ${tokenResponse.accessToken}`;
		} catch (error) {
			if (error instanceof InteractionRequiredAuthError) {
				msalInstance.logoutRedirect();
			}
			// Other errors: let the request go through, response interceptor will handle 401
		}
	}

	config.headers["Accept-Language"] = i18n.language || LanguageCode.En.toLowerCase();

	return config;
});

// Add a response interceptor
apiClient.interceptors.response.use(
	response => response,
	async error => {
		const originalRequest = error.config;

		// Handle 401 Unauthorized - token expired or invalid
		// Only retry once to prevent infinite loops
		if (error.response?.status === 401 && !originalRequest._retry) {
			originalRequest._retry = true;

			let account = msalInstance.getActiveAccount();
			if (!account) {
				const accounts = msalInstance.getAllAccounts();
				if (accounts.length > 0) {
					account = accounts[0];
					msalInstance.setActiveAccount(account);
				}
			}

			if (account) {
				try {
					// Try to refresh token silently
					const tokenResponse = await msalInstance.acquireTokenSilent({
						account: account,
						scopes: loginRequest.scopes,
						forceRefresh: true
					});

					// Retry the original request with new token
					originalRequest.headers.Authorization = `Bearer ${tokenResponse.accessToken}`;
					return apiClient.request(originalRequest);
				} catch (refreshError) {
					// Token refresh failed - force logout
					console.error("Token refresh failed, forcing logout", refreshError);
					msalInstance.logoutRedirect();
					return Promise.reject(error);
				}
			} else {
				// No account found at all - force logout
				console.error("401 received but no account found, forcing logout");
				msalInstance.logoutRedirect();
				return Promise.reject(error);
			}
		}

		// Handle 403 Forbidden - the user is authenticated but their current
		// access token is missing a role it should already hold. Right after
		// sign-up the backend adds the user to their Azure AD group (CLIENT /
		// FREELANCER) only once the account is created, so the token minted at
		// sign-up predates that membership and carries no role claim yet. Force
		// a one-time silent refresh to mint a fresh token that includes the new
		// role, then retry. Unlike a 401 we never log the user out on failure:
		// a genuine 403 (truly insufficient rights) must surface as a normal
		// error, not eject an authenticated user.
		if (error.response?.status === 403 && !originalRequest._retry) {
			originalRequest._retry = true;

			let account = msalInstance.getActiveAccount();
			if (!account) {
				const accounts = msalInstance.getAllAccounts();
				if (accounts.length > 0) {
					account = accounts[0];
					msalInstance.setActiveAccount(account);
				}
			}

			if (account) {
				try {
					// Bypass the MSAL cache so the freshly added group
					// membership is reflected in the new token.
					const tokenResponse = await msalInstance.acquireTokenSilent({
						account: account,
						scopes: loginRequest.scopes,
						forceRefresh: true
					});

					originalRequest.headers.Authorization = `Bearer ${tokenResponse.accessToken}`;
					return apiClient.request(originalRequest);
				} catch (refreshError) {
					// Refresh failed - fall through to the centralised error
					// handling below. Do NOT log out: the user is authenticated.
					console.error("Token refresh after 403 failed", refreshError);
				}
			}
			// Still forbidden (refresh failed, no account, or genuinely
			// insufficient permissions): let the error propagate normally.
		}

		// Continue to propagate the error
		return Promise.reject(error);
	}
);

export default apiClient;
